"use client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
    IconArticle,
    IconCirclesRelation,
    IconDotsVertical,
    IconFile,
    IconLoader2,
    IconX,
} from "@tabler/icons-react";
import React from "react";
import { IAccountShortLinks, IPages, IUserData } from "@/types";
import { UserURL } from "@/functions";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface Props {
    account: IUserData;
    linkSelect: (url: string) => void; // Define the prop type
}

export default function LinkSelectorDropdown({ account, linkSelect }: Props) {
    const [pages, setPages] = React.useState<IPages[]>([]);
    const [shortlinks, setShortlinks] = React.useState<IAccountShortLinks[]>(
        []
    );
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (isOpen) {
            setIsLoading(true);

            // Create promises for both fetch operations
            const fetchPages = fetch(`/api/user/${account.username}/pages`, {
                method: "GET",
            })
                .then(async (res) => await res.json())
                .then((pages) => {
                    console.log("🚀 ~ fetch ~ pages:", pages);
                    setPages(pages);
                })
                .catch(console.error);

            const fetchShortlinks = fetch(
                `/api/user/${account.username}/shortlinks`,
                { method: "GET" }
            )
                .then(async (res) => await res.json())
                .then((shortlinks) => {
                    console.log("🚀 ~ fetch ~ shortlinks:", shortlinks);
                    setShortlinks(shortlinks);
                })
                .catch(console.error);

            // Wait for both promises to resolve
            Promise.all([fetchPages, fetchShortlinks]).finally(() => {
                setIsLoading(false);
            });
        } else {
            setPages([]); // Clear pages when dropdown is closed
            setShortlinks([]);
        }
    }, [isOpen, account.username]);
    const cleanUrlForDisplay = (url: string): string => {
        return url.replace(/^https?:\/\//, "");
    };
    return (
        <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
            <DialogTrigger asChild>
                <Button className="h-10 w-10 bg-accent p-2">
                    <IconDotsVertical size={18} />
                </Button>
            </DialogTrigger>
            <DialogContent className="z-[9999999] h-96 max-h-96 overflow-auto p-0 sm:h-fit md:h-[40dvh] md:max-h-[40dvh]">
                <DialogHeader className="sr-only">
                    <DialogTitle className="sr-only">Quick links</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex h-full flex-col place-content-center items-center justify-center gap-2 py-6">
                        <IconLoader2 className="animate-spin" size={24} />
                        <p className="text-sm text-muted-foreground">
                            Loading...
                        </p>
                    </div>
                ) : (
                    <Tabs defaultValue="pages" className="w-full">
                        <TabsList className="sticky top-0 z-50 w-full border-b bg-card px-2">
                            <TabsTrigger value="pages">Pages</TabsTrigger>
                            <TabsTrigger value="shortlinks">
                                Shortlinks
                            </TabsTrigger>
                            <DialogClose className="ml-auto">
                                <IconX size={18} />
                            </DialogClose>
                        </TabsList>

                        <TabsContent value="pages" className=" p-3">
                            <Label variant={"subtoggle"}>
                                Link to one of your existing pages
                            </Label>
                            <div className="flex flex-col gap-1">
                                <div
                                    className="flex cursor-pointer items-center gap-2 rounded-md bg-muted p-2 hover:bg-muted/80"
                                    onClick={() => {
                                        linkSelect(
                                            "https://" +
                                                UserURL(account) +
                                                "/" +
                                                "blog"
                                        );
                                        setIsOpen(false);
                                    }}
                                >
                                    <IconArticle
                                        size={28}
                                        className="flex-shrink-0 text-muted-foreground"
                                    />
                                    <div className="flex flex-col">
                                        <span className="line-clamp-1 text-sm font-semibold">
                                            Blog
                                        </span>
                                        <span className="line-clamp-1 text-xs text-muted-foreground">
                                            {UserURL(account)}/blog
                                        </span>
                                    </div>
                                </div>
                                {pages.length === 0 ? (
                                    <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                        No pages found
                                    </div>
                                ) : (
                                    pages.map((page, index) => (
                                        <div
                                            key={index}
                                            className="flex cursor-pointer items-center gap-2 rounded-md bg-muted p-2 hover:bg-muted/80"
                                            onClick={() => {
                                                linkSelect(
                                                    "https://" +
                                                        UserURL(account) +
                                                        "/" +
                                                        page.slug
                                                );
                                                setIsOpen(false);
                                            }}
                                        >
                                            <IconFile
                                                size={28}
                                                className="flex-shrink-0 text-muted-foreground"
                                            />
                                            <div className="flex flex-col">
                                                <span className="line-clamp-1 text-sm font-medium">
                                                    {page.name}
                                                </span>
                                                <span className="line-clamp-1 text-xs font-medium text-muted-foreground">
                                                    {UserURL(account)}/
                                                    {page.slug}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="shortlinks" className="p-3">
                            <Label variant={"subtoggle"}>
                                Link to one of your short links
                            </Label>
                            <div className="flex flex-col gap-1">
                                {shortlinks.length === 0 ? (
                                    <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                        No short links found
                                    </div>
                                ) : (
                                    shortlinks.map((shortlink, index) => (
                                        <div
                                            key={index}
                                            className="flex cursor-pointer items-center gap-2 rounded-md bg-muted p-2 hover:bg-muted/80"
                                            onClick={() => {
                                                linkSelect(
                                                    shortlink.redirect_url
                                                );
                                                setIsOpen(false);
                                            }}
                                        >
                                            <IconCirclesRelation
                                                size={28}
                                                className="flex-shrink-0 text-muted-foreground"
                                            />
                                            <div className="flex flex-col">
                                                <span className="line-clamp-1 text-sm font-semibold">
                                                    {cleanUrlForDisplay(
                                                        shortlink.redirect_url
                                                    )}
                                                </span>
                                                <span className="line-clamp-1 text-xs text-muted-foreground">
                                                    {cleanUrlForDisplay(
                                                        shortlink.url
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                )}
            </DialogContent>
        </Dialog>
    );
}
